import { Leaf } from "@phosphor-icons/react";

function LegalShell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-white border-b border-[#EAEAEA] py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <Leaf size={18} weight="duotone" className="text-white" />
          </div>
          <span className="font-heading text-lg font-medium">Bookkeeping Portal</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto py-12 px-6">
        <h1 className="font-heading text-3xl font-medium mb-6" data-testid="legal-page-title">{title}</h1>
        <div className="space-y-4 text-sm leading-relaxed text-[#333]">
          {children}
        </div>
        <p className="text-xs text-[#888] mt-12">Last updated: April 27, 2026</p>
      </main>
    </div>
  );
}

export function Eula() {
  return (
    <LegalShell title="End-User License Agreement">
      <p>This End-User License Agreement ("Agreement") is between Todd's Nursery and North Star Grounds Care ("Company") and the authorized user ("User") accessing the Bookkeeping Portal ("Service"). By accessing the Service, the User agrees to the terms below.</p>

      <h2 className="font-heading text-lg font-medium mt-6">1. Limited License</h2>
      <p>The Company grants the User a limited, non-exclusive, non-transferable, revocable license to access the Service solely for internal bookkeeping coordination between the Company, its employees, and its authorized accounting providers.</p>

      <h2 className="font-heading text-lg font-medium mt-6">2. Acceptable Use</h2>
      <p>Users may not (a) share login credentials, (b) attempt to access data outside their assigned company scope, (c) reverse-engineer or copy the Service, or (d) use the Service for any purpose other than its intended bookkeeping function.</p>

      <h2 className="font-heading text-lg font-medium mt-6">3. QuickBooks Online Integration</h2>
      <p>When connected, the Service reads accounting data (open invoices, customer records, deposit records) from QuickBooks Online and may write payment and deposit records back to QuickBooks Online with explicit user approval. The User is responsible for reviewing all data before applying changes to their QuickBooks Online account.</p>

      <h2 className="font-heading text-lg font-medium mt-6">4. Data &amp; Confidentiality</h2>
      <p>All financial data accessed through the Service is confidential. The Company maintains administrative, technical, and physical safeguards to protect this data.</p>

      <h2 className="font-heading text-lg font-medium mt-6">5. Termination</h2>
      <p>The Company may revoke a User's access at any time. The User may disconnect their QuickBooks Online connection at any time from the Settings page.</p>

      <h2 className="font-heading text-lg font-medium mt-6">6. Disclaimer</h2>
      <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. THE COMPANY IS NOT LIABLE FOR ACCOUNTING DECISIONS MADE BASED ON DATA DISPLAYED IN THE SERVICE.</p>

      <h2 className="font-heading text-lg font-medium mt-6">7. Contact</h2>
      <p>For questions about this Agreement, contact zach@toddsnursery.com.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>Bookkeeping Portal ("we", "our", "us") is operated by Todd's Nursery and North Star Grounds Care for internal bookkeeping use. This Privacy Policy describes how we collect, use, and protect data accessed through the Service.</p>

      <h2 className="font-heading text-lg font-medium mt-6">1. Information We Collect</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Account information:</strong> name, email, role.</li>
        <li><strong>Uploaded documents:</strong> bank statements, payment advices, vendor statements, and supporting files uploaded by authorized users.</li>
        <li><strong>QuickBooks Online data:</strong> when connected, we access company info, customers, open invoices, deposits, and payments via the Intuit API. We never collect login credentials for QuickBooks.</li>
        <li><strong>Activity logs:</strong> actions performed in the Service (uploads, approvals, comments) for audit and compliance purposes.</li>
      </ul>

      <h2 className="font-heading text-lg font-medium mt-6">2. How We Use the Information</h2>
      <p>Data is used solely to (a) coordinate monthly bookkeeping between owners and bookkeepers, (b) match payment advices to open invoices in QuickBooks Online, and (c) maintain an audit trail. We do not sell, rent, or share data with advertisers.</p>

      <h2 className="font-heading text-lg font-medium mt-6">3. Storage &amp; Security</h2>
      <p>Data is stored on secured cloud infrastructure with encryption in transit (HTTPS/TLS) and at rest. Access is restricted to authenticated users with role-based permissions. Authentication tokens are stored in HTTP-only cookies.</p>

      <h2 className="font-heading text-lg font-medium mt-6">4. QuickBooks Online Tokens</h2>
      <p>OAuth refresh tokens issued by Intuit are stored encrypted and used only to access data on behalf of the company that authorized the connection. Tokens are revoked immediately when the user disconnects QuickBooks from the Settings page.</p>

      <h2 className="font-heading text-lg font-medium mt-6">5. Data Retention</h2>
      <p>Documents and activity logs are retained for the duration of the bookkeeping engagement plus seven (7) years as required for tax recordkeeping. Authorized owners may request deletion at any time.</p>

      <h2 className="font-heading text-lg font-medium mt-6">6. Disconnecting QuickBooks Online</h2>
      <p>Users can disconnect the QuickBooks Online integration at any time from the Settings page within the Service. Upon disconnection, the OAuth refresh token is revoked and we no longer access QuickBooks data.</p>

      <h2 className="font-heading text-lg font-medium mt-6">7. Children's Privacy</h2>
      <p>The Service is not directed at children under 13 and does not knowingly collect data from them.</p>

      <h2 className="font-heading text-lg font-medium mt-6">8. Contact</h2>
      <p>For questions about this Privacy Policy or to request data deletion, contact zach@toddsnursery.com.</p>
    </LegalShell>
  );
}

export function Disconnect() {
  return (
    <LegalShell title="QuickBooks Disconnected">
      <p>Your QuickBooks Online connection has been removed from Bookkeeping Portal.</p>
      <p>Bookkeeping Portal will no longer access any data from your QuickBooks Online account, and the OAuth refresh token has been revoked.</p>
      <p>If you would like to reconnect QuickBooks Online, sign in to Bookkeeping Portal and visit the Settings page.</p>
    </LegalShell>
  );
}
